import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle, ChevronRight, Star, Trophy, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import confetti from 'canvas-confetti';

const API = process.env.REACT_APP_BACKEND_URL;
const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

const CustomAssessmentPage = ({ user }) => {
  const navigate = useNavigate();
  const { assessmentId } = useParams();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [availableList, setAvailableList] = useState(null);

  useEffect(() => {
    if (assessmentId) {
      fetchAssessment();
    } else {
      fetchAvailableList();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId]);

  const fetchAvailableList = async () => {
    try {
      const res = await fetch(`${API}/api/ai/custom-assessments/available`, { headers: hdr() });
      if (res.ok) setAvailableList(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchAssessment = async () => {
    try {
      const res = await fetch(`${API}/api/ai/custom-assessments/${assessmentId}`, { headers: hdr() });
      if (res.ok) {
        const data = await res.json();
        if (data.already_completed) {
          toast.info('You already completed this assessment');
          navigate(-1);
          return;
        }
        setAssessment(data.assessment);
      } else {
        toast.error('Assessment not found');
        navigate(-1);
      }
    } catch (e) { toast.error('Failed to load'); }
    setLoading(false);
  };

  const selectAnswer = (questionId, answer) => {
    setAnswers({ ...answers, [questionId]: answer });
  };

  const submitAssessment = async () => {
    const questions = assessment.questions || [];
    const responses = questions.map(q => ({
      question_id: q.id,
      answer: answers[q.id] || ''
    })).filter(r => r.answer);

    if (responses.length < questions.length * 0.8) {
      toast.error(`Please answer at least ${Math.ceil(questions.length * 0.8)} questions`);
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/ai/custom-assessments/${assessmentId}/submit`, {
        method: 'POST', headers: hdr(), body: JSON.stringify({ responses })
      });
      if (res.ok) {
        const data = await res.json();
        setResults(data);
        confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
        toast.success(`Assessment complete! +${data.points_earned} points`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Submission failed');
      }
    } catch (e) { toast.error('Failed to submit'); }
    setSubmitting(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F3EF' }}>
      <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
    </div>
  );

  // List view (no assessmentId)
  if (!assessmentId && availableList) {
    return (
      <div className="min-h-screen pb-24" style={{ backgroundColor: '#F5F3EF' }}>
        <div style={{ background: 'linear-gradient(135deg, #6366F1, #8B5CF6)' }} className="p-6 text-white rounded-b-3xl">
          <div className="flex items-center gap-3 mb-2">
            <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-white/20"><ArrowLeft className="w-5 h-5" /></button>
            <h1 className="text-xl font-bold">Assessments</h1>
          </div>
          <p className="text-white/70 text-sm">Test your skills and earn points</p>
        </div>
        <div className="max-w-lg mx-auto px-4 py-5 space-y-3">
          {availableList.length === 0 && (
            <Card className="p-8 text-center rounded-2xl">
              <Star className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-muted-foreground">No assessments available right now</p>
            </Card>
          )}
          {availableList.map(a => (
            <Card
              key={a.id}
              className="p-4 rounded-2xl cursor-pointer hover:shadow-md transition-all"
              onClick={() => !a.completed && navigate(`/custom-assessment/${a.id}`)}
              data-testid={`assessment-card-${a.id}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${a.completed ? 'bg-green-100' : 'bg-violet-100'}`}>
                  {a.completed ? <CheckCircle className="w-5 h-5 text-green-600" /> : <Star className="w-5 h-5 text-violet-600" />}
                </div>
                <div className="flex-1">
                  <p className="font-bold text-sm">{a.title}</p>
                  <p className="text-xs text-muted-foreground">{a.question_count} questions • {a.points_awarded} pts</p>
                </div>
                {a.completed ? (
                  <Badge className="bg-green-100 text-green-700">Done</Badge>
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
              </div>
            </Card>
          ))}
        </div>
        <Navigation role="youth" />
      </div>
    );
  }

  // Results view
  if (results) {
    return (
      <div className="min-h-screen pb-24" style={{ backgroundColor: '#F5F3EF' }}>
        <div style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }} className="py-8 px-4 text-center text-white rounded-b-3xl">
          <Trophy className="w-14 h-14 mx-auto mb-2 text-yellow-300" />
          <h1 className="text-2xl font-bold">Assessment Complete!</h1>
          <p className="text-4xl font-black mt-2">{Math.round(results.score)}%</p>
          <p className="text-white/70 text-sm">+{results.points_earned} points earned</p>
        </div>
        <div className="max-w-lg mx-auto px-4 py-5 space-y-4">
          {results.domain_scores && Object.keys(results.domain_scores).length > 0 && (
            <Card className="p-4 rounded-2xl">
              <h3 className="font-bold text-sm mb-3">Score Breakdown</h3>
              {Object.entries(results.domain_scores).map(([domain, data]) => (
                <div key={domain} className="mb-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium capitalize">{domain.replace(/_/g, ' ')}</span>
                    <span className="font-bold">{Math.round(data.score)}%</span>
                  </div>
                  <Progress value={data.score} className="h-2" />
                </div>
              ))}
            </Card>
          )}
          {results.ai_feedback?.strengths?.length > 0 && (
            <Card className="p-4 rounded-2xl bg-green-50">
              <p className="text-xs font-bold text-green-700 mb-2">Your Strengths</p>
              {results.ai_feedback.strengths.map((s, i) => <p key={i} className="text-sm text-green-800 mb-1">+ {s}</p>)}
            </Card>
          )}
          {results.ai_feedback?.growth_areas?.length > 0 && (
            <Card className="p-4 rounded-2xl bg-amber-50">
              <p className="text-xs font-bold text-amber-700 mb-2">Areas to Develop</p>
              {results.ai_feedback.growth_areas.map((g, i) => <p key={i} className="text-sm text-amber-800 mb-1">~ {g}</p>)}
            </Card>
          )}
          <Button onClick={() => navigate('/dashboard')} className="w-full rounded-xl h-12 bg-violet-600 hover:bg-violet-700 text-white font-bold">
            Back to Dashboard
          </Button>
        </div>
        <Navigation role="youth" />
      </div>
    );
  }

  // Taking assessment view
  if (!assessment) return null;
  const questions = assessment.questions || [];
  const q = questions[currentQ];
  const progress = ((currentQ + 1) / questions.length) * 100;
  const isLast = currentQ === questions.length - 1;
  const allAnswered = questions.every(q => answers[q.id]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F3EF' }}>
      <div className="sticky top-0 z-40 bg-white shadow-sm px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <button onClick={() => { if (window.confirm('Leave? Your progress will be lost.')) navigate(-1); }} className="text-sm text-muted-foreground">Cancel</button>
          <p className="text-xs font-bold">{currentQ + 1} of {questions.length}</p>
          <p className="text-xs font-bold text-violet-600">{assessment.title}</p>
        </div>
        <Progress value={progress} className="h-1.5" />
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {q && (
          <motion.div key={currentQ} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <div className="mb-2">
              <Badge className="bg-violet-100 text-violet-700 text-[10px] mb-2">{q.category?.replace(/_/g, ' ')}</Badge>
              <h2 className="text-lg font-bold">{q.question_text}</h2>
            </div>

            <div className="space-y-2 mt-4">
              {(q.options || []).map((opt, i) => (
                <button
                  key={i}
                  onClick={() => selectAnswer(q.id, opt)}
                  className={`w-full text-left p-4 rounded-xl transition-all border ${
                    answers[q.id] === opt
                      ? 'bg-violet-50 border-violet-400 ring-1 ring-violet-400'
                      : 'bg-white border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`option-${i}`}
                >
                  <p className="text-sm font-medium">{opt}</p>
                </button>
              ))}
            </div>
          </motion.div>
        )}

        <div className="flex gap-3 mt-6">
          {currentQ > 0 && (
            <Button variant="outline" onClick={() => setCurrentQ(currentQ - 1)} className="rounded-xl">Back</Button>
          )}
          {!isLast ? (
            <Button
              onClick={() => setCurrentQ(currentQ + 1)}
              disabled={!answers[q?.id]}
              className="flex-1 rounded-xl bg-violet-600 hover:bg-violet-700 text-white"
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={submitAssessment}
              disabled={submitting || !allAnswered}
              className="flex-1 rounded-xl bg-green-600 hover:bg-green-700 text-white font-bold"
              data-testid="submit-assessment-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Submit Assessment
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomAssessmentPage;
